// Eskom PARCC — status icons + legend (owner R3: statuses as icons, not long
// text pills). The pure classification lives in domain/statusMeta.ts; here we map
// its iconKey → a lucide glyph and tone → a colour token, and render an
// accessible icon (role=img + aria-label/title carrying the full status text) plus
// a compact, collapsible legend so the icon language is always decodable.
import {
  CheckCircle2,
  AlertTriangle,
  FileWarning,
  Wrench,
  MinusCircle,
  Clock,
  Circle,
  CircleDot,
  CircleCheck,
  Loader2,
  Trash2,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  statusMeta,
  reviewedMeta,
  COMPLIANCE_LEGEND,
  REVIEW_LEGEND,
  GAP_STATUS_LEGEND,
  type IconKey,
  type Tone,
  type StatusMeta,
} from '../../domain/statusMeta';

const ICONS: Record<IconKey, LucideIcon> = {
  ok: CheckCircle2,
  ie: AlertTriangle,
  policy: FileWarning,
  housekeeping: Wrench,
  na: MinusCircle,
  pending: Clock,
  reviewed: CheckCircle2,
  notReviewed: Circle,
  progress: Loader2,
  open: CircleDot,
  closed: CircleCheck,
  deleted: Trash2,
  unknown: HelpCircle,
};

const TONE_VAR: Record<Tone, string> = {
  ok: 'var(--ok)',
  bad: 'var(--bad)',
  warn: 'var(--warn)',
  house: 'var(--house)',
  na: 'var(--na)',
  neutral: 'var(--muted)',
};

/** A single tone-coloured glyph for a given iconKey (no wrapper semantics). */
function Glyph({ iconKey, tone, size = 16 }: { iconKey: IconKey; tone: Tone; size?: number }): React.JSX.Element {
  const Icon = ICONS[iconKey];
  return <Icon size={size} color={TONE_VAR[tone]} aria-hidden />;
}

/** Render a resolved StatusMeta as an accessible icon (full label on hover + a11y). */
function MetaIcon({ meta, size }: { meta: StatusMeta; size?: number }): React.JSX.Element {
  return (
    <span
      className={`status-ic status-ic--${meta.tone}${meta.selfDeclared ? ' is-self' : ''}`}
      role="img"
      aria-label={meta.label}
      title={meta.label}
    >
      <Glyph iconKey={meta.iconKey} tone={meta.tone} size={size} />
    </span>
  );
}

/**
 * Status as an icon. Pass a compliance/CES level string via `value`, OR the
 * boolean review flag via `reviewed`. Self-declared levels get a subtle ring.
 */
export function StatusIcon({
  value,
  reviewed,
  size = 16,
}: {
  value?: string | null;
  reviewed?: boolean;
  size?: number;
}): React.JSX.Element {
  const meta = typeof reviewed === 'boolean' ? reviewedMeta(reviewed) : statusMeta(value);
  return <MetaIcon meta={meta} size={size} />;
}

/**
 * A compact, always-visible key for the icon language. `kind` selects the
 * compliance buckets (default) or the review-status buckets. The items lay out as
 * a clean wrapping row so the legend is decodable at a glance (no click to open);
 * the per-item sizing is the same compact scale as before. Place it in a section
 * head — it wraps below the title/actions on narrow widths.
 */
export function StatusLegend({
  kind = 'compliance',
}: {
  kind?: 'compliance' | 'review' | 'gapStatus';
}): React.JSX.Element {
  const items =
    kind === 'review' ? REVIEW_LEGEND : kind === 'gapStatus' ? GAP_STATUS_LEGEND : COMPLIANCE_LEGEND;
  return (
    <div className="status-legend" role="list" aria-label="Status legend">
      {items.map((it) => (
        <span className="status-legend-item" role="listitem" key={it.label} title={it.hint}>
          <span className={`status-ic status-ic--${it.tone}${it.selfDeclared ? ' is-self' : ''}`}>
            <Glyph iconKey={it.iconKey} tone={it.tone} size={15} />
          </span>
          {it.label}
        </span>
      ))}
    </div>
  );
}
